import React, { Component, FC } from 'react';
import { ITransferDetailProps } from '../../types/propTypes';
import { ITransferDetailState } from '../../types/stateTypes';
import { IInvoiceDetail } from '../../models/organisationTransaction';
import { Common } from '../../utils/constants';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import { CSVLink } from 'react-csv';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Can } from '../../ability';
import Table from '../../components/table/Table';
import StandardResponse from '../../models/standard-response';
import images from '../../assets/images/index';
import { companyNameReplace, regionDateAndTime } from '../../utils';
import {
    getCurrencyFormat,
    getFormattedCurrencyWithSymbol,
    getNumberFormat,
    localFormat,
} from '../../components/common/LocalNumberFormat';
import { store } from '../../store';
import CPOInternalTransfersServices from '../../services/cpo-internal-transfers/cpo-internal-transfers-service';
import { ITransferData, ITransferDetail } from '../../models/cpo-internal-transfers/index';
import { useParams } from 'react-router';

const TransferDetailsHOC: FC<ITransferDetailProps & WithTranslation> = (props) => {
    const match = { params: useParams() };

    return <TransferDetails {...props} match={match} />;
};

export class TransferDetails extends Component<ITransferDetailProps & WithTranslation, ITransferDetailState> {
    cpoInternalTransfersService: CPOInternalTransfersServices;
    exportBtn: React.RefObject<CSVLink> = React.createRef();
    maxHeight;
    constructor(props) {
        super(props);

        this.state = {
            columns: [],
            data: [],
            currentPage: 1,
            offset: 0,
            rowsPerPage: 10,
            sortColumn: 'transaction_start_date',
            sortOrder: 'desc',
            totalCount: 0,
            vatDetail: [],
            net_value: '',
            total_amount: '',
            transferDate: null,
            transferNumber: '',
            adjusted_amount: '',
            currency_details: null,
        };
        this.cpoInternalTransfersService = new CPOInternalTransfersServices();
    }

    async componentDidMount() {
        this.maxHeight = window.innerHeight - document.getElementsByClassName('header')[0].clientHeight - 300;

        const { t } = this.props;
        const header = [
            {
                label: t('CPOInternalTransfers.label.transaction'),
                id: 'transaction_id',
                sortable: true,
                isVisible: true,
            },
            {
                label: t('CPOInternalTransfers.label.transactionStartDate'),
                id: 'transaction_start_date',
                sortable: true,
                isVisible: true,
                cell: (row: IInvoiceDetail) => {
                    if (row.transaction_start_date) {
                        return moment(row.transaction_start_date).format(regionDateAndTime().GENERAL_DATE_TIME_FORMAT);
                    }
                    return null;
                },
            },
            {
                label: t('CPOInternalTransfers.label.transactionEndDate'),
                id: 'transaction_end_date',
                sortable: true,
                isVisible: true,
                cell: (row: IInvoiceDetail) => {
                    if (row.transaction_end_date) {
                        return moment(row.transaction_end_date).format(regionDateAndTime().GENERAL_DATE_TIME_FORMAT);
                    }
                    return null;
                },
            },
            {
                label: t('CPOInternalTransfers.label.publicCardId'),
                id: 'public_card_id',
                sortable: true,
                isVisible: true,
            },
            {
                label: t('CPOInternalTransfers.label.driverName'),
                id: 'driver_name',
                sortable: true,
                isVisible: true,
                style: { maxWidth: 120 },
                cell: (row: IInvoiceDetail) => {
                    return (
                        <span className="d-block text-truncate" title={row.driver_name}>
                            {row.driver_name}
                        </span>
                    );
                },
            },
            {
                label: t('CPOInternalTransfers.label.stationId'),
                id: 'station_id',
                sortable: true,
                isVisible: true,
                style: { maxWidth: 150 },
                cell: (row: IInvoiceDetail) => {
                    return (
                        <span className="d-block text-truncate" title={row.station_id}>
                            {row.station_id}
                        </span>
                    );
                },
            },
            {
                label: t('CPOInternalTransfers.label.stationName'),
                id: 'station_name',
                sortable: true,
                isVisible: true,
                style: { maxWidth: 250 },
                cell: (row: IInvoiceDetail) => {
                    return (
                        <span className="d-block text-truncate" title={row.station_name}>
                            {row.station_name}
                        </span>
                    );
                },
            },
            {
                label: t('CPOInternalTransfers.label.stationAddress'),
                id: 'station_address',
                sortable: true,
                isVisible: true,
                style: { maxWidth: 300 },
                cell: (row: IInvoiceDetail) => {
                    return (
                        <span className="d-block text-truncate" title={row.station_address}>
                            {row.station_address}
                        </span>
                    );
                },
            },
            {
                label: t('CPOInternalTransfers.label.kwh'),
                id: 'kwh',
                sortable: true,
                isVisible: true,
                cell: (row: IInvoiceDetail) => {
                    return localFormat(row?.kwh?.toString(), 'string', getNumberFormat()?.FINANCIAL_NUMBER_FORMAT);
                },
            },
            {
                label: t('CPOInternalTransfers.label.price_per_kwh'),
                id: 'price_per_kwh',
                sortable: true,
                isVisible: true,
                style: { textAlign: 'right' },
                cell: (row: IInvoiceDetail) => {
                    if (row.price_per_kwh) {
                        return (
                            <>
                                {getFormattedCurrencyWithSymbol(
                                    row.price_per_kwh?.toString(),
                                    'text',
                                    getCurrencyFormat()?.FINANCIAL_CURRENCY_FORMAT,
                                    row.currency_details
                                        ? row.currency_details.symbol
                                        : store?.getState()?.configuration?.default_currency?.symbol,
                                    false,
                                    true,
                                )}
                            </>
                        );
                    }
                    return null;
                },
            },
            {
                label: t('CPOInternalTransfers.label.gross_value'),
                id: 'gross_value',
                sortable: true,
                isVisible: true,
                style: { textAlign: 'right' },
                cell: (row: ITransferDetail) => {
                    if (row.gross_value) {
                        return (
                            <>
                                {getFormattedCurrencyWithSymbol(
                                    row.gross_value?.toString(),
                                    'text',
                                    getCurrencyFormat()?.FINANCIAL_CURRENCY_FORMAT,
                                    row.currency_details
                                        ? row.currency_details.symbol
                                        : store?.getState()?.configuration?.default_currency?.symbol,
                                    false,
                                    true,
                                )}
                            </>
                        );
                    }
                    return null;
                },
            },
        ];
        this.setState(
            {
                columns: header,
            },
            async () => {
                document.getElementById('loading').classList.remove('spinner-disable');
                const list = await this.getTransferDetail();
                this.setState({
                    data: list,
                });
                document.getElementById('loading').classList.add('spinner-disable');
            },
        );
    }
    getTransferDetail = async () => {
        const filterInput = {
            limit: this.state.rowsPerPage,
            offset: this.state.offset,
            order: [[this.state.sortColumn, this.state.sortOrder]],
        };

        let data: any = [];
        await this.cpoInternalTransfersService
            .getTransferDetail(filterInput, (this.props as any).match.params.id)
            .then(async (res: StandardResponse<ITransferData>) => {
                data = res.Data.transaction_details;
                this.setState({
                    totalCount: res.Data.total_count,
                    total_amount: res.Data.total_amount,
                    net_value: res.Data.net_amount,
                    transferDate: res.Data.transfer_date,
                    transferNumber: res.Data.transfer_id,
                    adjusted_amount: res.Data.adjusted_value,
                    currency_details: res?.Data?.currency_details,
                });
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            });

        return data;
    };
    handleSorting = async (columnName: string) => {
        let sortOrder = this.state.sortOrder;
        if (this.state.sortColumn === columnName) {
            sortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
        } else {
            sortOrder = 'asc';
        }

        this.setState(
            {
                sortOrder: sortOrder,
                sortColumn: columnName,
            },
            async () => {
                const list = await this.getTransferDetail();
                this.setState({
                    data: list,
                });
            },
        );
    };
    handlePageChange = async (currentPage: number, rowsPerPage: number) => {
        const offset = (currentPage - 1) * rowsPerPage;
        this.setState(
            {
                currentPage: currentPage,
                rowsPerPage: rowsPerPage,
                offset: offset,
            },
            async () => {
                const list = await this.getTransferDetail();
                this.setState({
                    data: list,
                });
            },
        );
    };
    handleExportAllData = async () => {
        const csvLink = await this.getTransferDetailListCsvLink();
        if (csvLink !== '') {
            window.open(csvLink, '_blank');
        }
    };
    getTransferDetailListCsvLink = async () => {
        const filterInput = { order: [[this.state.sortColumn, this.state.sortOrder]] };
        let csvLink = '';

        await this.cpoInternalTransfersService
            .getTransferDetailsListCsvLink((this.props as any).match.params.id, filterInput)
            .then(async (res: StandardResponse<string>) => {
                csvLink = res.Data;
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            });
        return csvLink;
    };
    render() {
        const { t } = this.props;
        return (
            <>
                <Helmet>
                    <title>{`${companyNameReplace(t('CPOInternalTransfers.transferDetailTitle'))}`}</title>
                </Helmet>
                <div className="title row align-items-content justify-content-between pb-3">
                    <div className="col-auto col-sm text-truncate pr-0">
                        <h1 className="text-truncate">{t('CPOInternalTransfers.transferDetailHeader')}</h1>
                        <br />
                        <span>{`${t('CPOInternalTransfers.label.transferNumber')}: ${this.state.transferNumber}`}</span>
                        <br />
                        <br />
                        <span>{`${t('CPOInternalTransfers.label.transferDate')}: ${moment(
                            this.state.transferDate,
                        ).format(regionDateAndTime().GENERAL_DATE_TIME_FORMAT)}`}</span>
                    </div>
                    <div className="col-auto">
                        <Can I={Common.Actions.CAN_EXPORT} a={Common.Modules.CPOS.CPOS_INTERNAL_TRANSFER}>
                            <div className="btn-list col-auto float-right">
                                <button
                                    onClick={this.handleExportAllData}
                                    className="btn icon-btn pr-0 btn-outline-primary ml-3"
                                >
                                    <img
                                        className="icon"
                                        src={images.dashboardBtnSVG}
                                        alt={t('organisationtransactions.button.export')}
                                        title={t('organisationtransactions.button.export')}
                                    />
                                </button>
                            </div>
                        </Can>
                        <div className="balance-info text-right">
                            <label className={'label-color pr-3'} style={{ marginTop: '40px' }}>
                                {t('CPOInternalTransfers.label.gross_value')} :{' '}
                                <span className="themeColor" style={{ fontWeight: 'bold' }}>
                                    {getFormattedCurrencyWithSymbol(
                                        this.state?.total_amount?.toString(),
                                        'text',
                                        getCurrencyFormat()?.FINANCIAL_CURRENCY_FORMAT,
                                        this.state.currency_details?.symbol,
                                        false,
                                        true,
                                    )}
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
                <div className="card-outer">
                    <Table
                        columns={this.state.columns}
                        data={this.state.data}
                        rowsPerPage={this.state.rowsPerPage}
                        currentPage={this.state.currentPage}
                        totalCount={this.state.totalCount}
                        sortColumn={this.state.sortColumn}
                        sortOrder={this.state.sortOrder}
                        handlePageChange={this.handlePageChange}
                        handleSorting={this.handleSorting}
                        t={this.props.t}
                        isCheckboxGrid={false}
                        allowPageing={true}
                        classNamesProps="remove-min-height"
                    />
                </div>
            </>
        );
    }
}

export const TransferDetailsClass = TransferDetails;
export default withTranslation()(TransferDetailsHOC);
